import { TextInput } from '@components/form/baseInputs'
import { useBrand } from '@contexts/useBrand'
import axios from 'axios'
import error from 'next/error'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

import DropDown from '../DropDown'
import YearsMonths from '../YearsMonths'

type Props = {
  prefix?: string
  label?: string
  helper?: string
  optionsLabel?: string
  optional?: boolean
  historic?: boolean
  historicLabel?: string
}

const AddressLookup = ({
  prefix = '',
  label = 'Postcode',
  optional,
  optionsLabel,
  helper,
  historic,
  historicLabel,
  ...rest
}: Props) => {
  const { getValues, setValue, formState, resetField } = useFormContext()
  const [suggestions, setSuggestions] = useState([])
  const [manualInput, openManualInput] = useState(false)
  const [searchTerm, setSearchterm] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [isDropdownSearching, setIsDropdownSearching] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const brand = useBrand()
  const { errors } = formState
  const handleShowManual = () => {
    openManualInput(!manualInput)
  }

  useEffect(() => {
    if (getValues(`${prefix}postcode`)) {
      openManualInput(true)
    }
  }, [manualInput])

  const handleAddress = (address) => {
    setValue(`${prefix}address_line1`, address.line_1, {
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue(`${prefix}address_line2`, address.line_2, {
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue(`${prefix}town`, address.town_or_city, {
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue(`${prefix}county`, address.county, {
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue(`${prefix}postcode`, address.postcode, {
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue(`${prefix}full_address`, address)
    openManualInput(true)
  }

  const handleNewSearch = () => {
    resetField(`${prefix}address_line1`)
    resetField(`${prefix}address_line2`)
    resetField(`${prefix}town`)
    resetField(`${prefix}county`)
    resetField(`${prefix}postcode`)
  }

  let apiToken
  switch (brand) {
    case 'lexus':
      apiToken = process.env.NEXT_PUBLIC_GET_ADDRESS_API_KEY_LEXUS
      break
    case 'kinto':
      apiToken = process.env.NEXT_PUBLIC_GET_ADDRESS_API_KEY_KINTO
      break
    default:
      apiToken = process.env.NEXT_PUBLIC_GET_ADDRESS_API_KEY_TOYOTA
      break
  }

  const getAddressList = async () => {
    handleNewSearch()
    setIsSearching(true)
    await axios({
      method: 'post',
      url: `https://api.getAddress.io/autocomplete/${searchTerm}?api-key=${apiToken}`,
    })
      .then((res) => {
        const parseAddresses = res.data.suggestions.map((suggestion) => {
          return {
            value: suggestion.id,
            label: suggestion.address,
          }
        })
        setSuggestions(parseAddresses)
      })
      .catch(() => {
        throw new Error('Network response was not ok')
      })
      .finally(() => {
        setIsSearching(false)
        if (searchTerm) {
          setShowSuggestions(true)
        }
      })
  }

  const onChange = async (e) => {
    const addressId = e.target.value
    setIsDropdownSearching(true)
    await axios({
      method: 'get',
      url: `https://api.getAddress.io/get/${addressId}?api-key=${apiToken}`,
    })
      .then((res) => {
        handleAddress(res.data)
        setIsDropdownSearching(false)
      })
      .catch(() => {
        throw new Error('Network response was not ok')
      })
  }

  return (
    <>
      <div
        className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 gap-4 items-start justify-top mb-4 md:mb-2"
        data-gt-name="componentevent"
      >
        <div className="md:col-span-2 lg:col-span-1">
          <label className="block">
            <span className="inline-block text-base-text mb-1">
              {label}{' '}
              {optional && (
                <span className="text-xs lowercase whitespace-nowrap">
                  (optional)
                </span>
              )}
            </span>
            <div className="relative grid grid-cols-6">
              <input
                className={twMerge(
                  `rounded-input border-input-border col-span-3 sm:col-span-4 max-w-full mr-4 p-[0.7rem]`,
                )}
                type="text"
                aria-invalid={error ? 'true' : 'false'}
                data-gt-action="text-input-interaction"
                value={searchTerm}
                onChange={(e) => setSearchterm(e.target.value)}
                {...rest}
              />
              <button
                onClick={getAddressList}
                type="button"
                className={`relative ${isSearching ? 'opacity-50' : ''} ${
                  brand === 'lexus'
                    ? 'col-span-3 sm:col-span-2 inline-flex items-center justify-center flex-none uppercase !bg-secondary text-white'
                    : 'col-span-3 sm:col-span-2 inline-flex items-center justify-center flex-none rounded-btn px-4 sm:px-6 text-sm sm:text-base !text-white !outline-0 bg-base hover:bg-base-hover active:bg-base-hover focus:bg-base-hover !disabled:cursor-not-allowed disabled:bg-base-hover'
                }`}
                data-gt-name="componentevent"
                data-gt-action="address-lookup-click-cta"
                data-gt-label="find address"
              >
                {isSearching && <div className="loader">Loading...</div>}
                <span className="text-tiny">Find Address</span>
              </button>
            </div>
          </label>
          <button
            type="button"
            className="mb-1 col-span-2 md:col-span-1 lg:col-span-2 text-base-text"
            onClick={handleShowManual}
          >
            {manualInput ? 'Find address' : 'Enter address manually'}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className={`w-[16px] inline-block ml-2 ${
                manualInput ? 'rotate-180' : ''
              }`}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 8.25l-7.5 7.5-7.5-7.5"
              />
            </svg>
          </button>
        </div>
        <div className="mt-1 md:mt-8 flex items-center md:min-h-[50px]">
          {helper && (
            <p
              className="text-tiny leading-5"
              dangerouslySetInnerHTML={{ __html: helper }}
            />
          )}
        </div>
      </div>
      <div>
        {isDropdownSearching && (
          <div className="loader-container absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="loader dark">Loading...</div>
          </div>
        )}
        {showSuggestions && (
          <DropDown
            name={`${prefix}suggestions`}
            options={suggestions}
            optionsLabel={optionsLabel}
            onChange={onChange}
          />
        )}
      </div>

      <div
        className={`${
          !manualInput && !(`${prefix}postcode` in errors) ? 'hidden' : ''
        }`}
      >
        <TextInput
          name={`${prefix}address_line1`}
          label="Address Line 1"
          {...rest}
        />
        <TextInput
          name={`${prefix}address_line2`}
          label="Address Line 2"
          {...rest}
        />
        <TextInput name={`${prefix}town`} label="Town/City" {...rest} />
        <TextInput name={`${prefix}county`} label="County" {...rest} />
        <TextInput name={`${prefix}postcode`} label="Postcode" {...rest} />
      </div>
      {historic && (
        <YearsMonths historicLabel={historicLabel} prefix={prefix} />
      )}
    </>
  )
}

export default AddressLookup
